// src/themes/apply.js
// Aplica un tema al DOM inyectando CSS custom properties en :root.
// Se llama desde main.js ANTES de `new Vue` para evitar flash.

import { getTheme, getEnabledThemes, DEFAULT_THEME } from './index'
import systemB from './system-b'

const STORAGE_KEY = 'kalan:theme'
const NON_TOKEN_KEYS = new Set(['id', 'label', 'mapBasemap', 'isLight'])

// ========== Murphy logo theming ==========
// Dos modos disponibles:
//   - 'neutral' (default): grises fijos, mismo logo en los 4 temas. Logo
//     se siente como marca de tercero, neutral, sobrio.
//   - 'themed': los tokens murphy* del tema ganan, el logo dialoga con
//     el accent del tema (cyan / lavanda / phosphor / neón).
// Los stakeholders alternan entre ambos para decidir cuál prefieren.

const MURPHY_MODE_KEY = 'kalan:murphy-mode'
const VALID_MURPHY_MODES = ['neutral', 'themed']
const DEFAULT_MURPHY_MODE = 'neutral'

const MURPHY_NEUTRAL = {
  murphyLight: '#F0F2F5',
  murphyMid: '#B8BDC5',
  murphyDeep1: '#6E747D',
  murphyDeep2: '#858C96',
  murphyInk: '#2A2E35',
}

// Helper functions
function kebabCase(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
}

function hexToRgbTriplet(hex) {
  if (typeof hex !== 'string') return null
  let h = hex.trim().replace('#', '')
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  if (h.length !== 6) return null
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return null
  return `${r}, ${g}, ${b}`
}

export function readMurphyMode() {
  let persisted = null
  try { persisted = localStorage.getItem(MURPHY_MODE_KEY) } catch (_) { /* private mode */ }
  if (VALID_MURPHY_MODES.includes(persisted)) return persisted
  return DEFAULT_MURPHY_MODE
}

export function persistMurphyModeAndReload(mode) {
  if (!VALID_MURPHY_MODES.includes(mode)) return
  try { localStorage.setItem(MURPHY_MODE_KEY, mode) } catch (_) { /* quota or private mode */ }
  window.location.reload()
}

export function readPersistedTheme() {
  let persisted = null
  try { persisted = localStorage.getItem(STORAGE_KEY) } catch (_) { /* private mode */ }
  const enabled = getEnabledThemes().map(t => t.id)
  if (persisted && enabled.includes(persisted)) return persisted
  if (enabled.includes(DEFAULT_THEME)) return DEFAULT_THEME
  return enabled[0] || DEFAULT_THEME
}

export function applyTheme(themeId) {
  const theme = getTheme(themeId)
  // Merge: Sistema B (taxonomía operativa invariante) primero, tema
  // (chrome/Sistema A) después. Si un tema necesitara overridear un token
  // de Sistema B, gana el tema — por ahora ninguno lo hace.
  // Murphy neutral, si está activo, gana al final sobre los murphy* del tema.
  const murphyMode = readMurphyMode()
  // En 'neutral' los grises Murphy ganan al final (overridean cualquier
  // murphy* del tema). En 'themed' los grises se mergean primero como
  // fallback, y el tema (si declara murphy*) los sobrescribe.
  const merged = murphyMode === 'neutral'
    ? { ...systemB, ...theme, ...MURPHY_NEUTRAL }
    : { ...systemB, ...MURPHY_NEUTRAL, ...theme }
  const root = document.documentElement
  Object.entries(merged).forEach(([key, value]) => {
    if (NON_TOKEN_KEYS.has(key)) return
    root.style.setProperty(`--kln-${kebabCase(key)}`, value)
  })
  // Tripletas r,g,b derivadas para usos rgba(var(--kln-*-rgb), a).
  // El analytics monitor (capa de alias) las consume; cualquier feature
  // futuro que necesite rgba() temático también.
  const RGB_DERIVE = {
    'accent-rgb': merged.accent,
    'alert-sos-rgb': merged.alertSos,
    'alert-success-rgb': merged.alertSuccess,
    'alert-warning-rgb': merged.alertWarning,
  }
  Object.entries(RGB_DERIVE).forEach(([name, hex]) => {
    const triplet = hexToRgbTriplet(hex)
    if (triplet) root.style.setProperty(`--kln-${name}`, triplet)
  })
  root.setAttribute('data-theme', theme.id)
  root.setAttribute('data-murphy-mode', murphyMode)
}

export function persistAndReload(themeId) {
  try { localStorage.setItem(STORAGE_KEY, themeId) } catch (_) { /* quota exceeded or private mode */ }
  window.location.reload()
}

export const THEME_CHANGE_CONFIRM = {
  title: 'Cambiar tema',
  description: 'La aplicación se recargará. Si tienes una llamada SOS activa o transmisiones de cámara, conviene terminarlas primero.',
  acceptText: 'Continuar',
  cancelText: 'Cancelar',
}

export const MURPHY_MODE_CHANGE_CONFIRM = {
  title: 'Cambiar logo Murphy',
  description: 'La aplicación se recargará para aplicar el cambio.',
  acceptText: 'Continuar',
  cancelText: 'Cancelar',
}
